import { WorkflowDashboardNavigationComponent } from "@app/workflow-dashboards/workflow-dashboard-navigation.component";
import { CommonModule } from "@angular/common";
import { Component, DestroyRef, ViewChild, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Validators } from "@angular/forms";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { EzFormComponent } from "@template-ui/components/ez-form/ez-form.component";
import { EzFormInputType, InputConfig } from "@template-ui/types/ez-form.types";
import { ButtonConfig, EzGridDsConfig, InputSelectOption, PanelHeaderConfig } from "@template-ui/types";
import { ColDef } from "ag-grid-community";
import { ButtonCellRenderer } from "@app/shared/components/tables/ag-grid-cells/button-cell-renderer/button-cell-renderer.component";
import { RoleService } from "@app/shared/services/roles.service";
import { Subscription } from "rxjs";
import {
  PlatformAccessAdminDisplayError,
  PlatformNavigationNodeCreateRequest,
  PlatformNavigationNodeDetail,
  PlatformNavigationNodeFormValues,
  PlatformNavigationNodeUpdateRequest,
  PlatformNavigationRuleDraft,
  PlatformNavigationRuleFormValues,
  PlatformNavigationRuleRequest,
} from "./platform-access-admin.models";
import {
  PlatformAccessAdminService,
  mapPlatformAccessAdminError,
} from "./platform-access-admin.service";

const PLATFORM_ACCESS_ADMIN_READ = "platform_access_admin.read";
const PLATFORM_ACCESS_ADMIN_AUTHOR = "platform_access_admin.author";
const PLATFORM_ACCESS_ADMIN_PUBLISH = "platform_access_admin.publish";
const PLATFORM_ACCESS_ADMIN_ROLLBACK = "platform_access_admin.rollback";

const NAVIGATION_NODE_TYPE_OPTIONS: InputSelectOption[] = [
  { value: "group", viewValue: "Group" },
  { value: "route", viewValue: "Route" },
];

const NAVIGATION_RULE_TYPE_OPTIONS: InputSelectOption[] = [
  { value: "permission", viewValue: "Permission" },
  { value: "feature_flag", viewValue: "Feature Flag" },
];

const NAVIGATION_VISIBILITY_MODE_OPTIONS: InputSelectOption[] = [
  { value: "required", viewValue: "Required" },
  { value: "optional_hint", viewValue: "Optional Hint" },
];

@Component({
    selector: "phms-platform-access-admin",
    imports: [CommonModule, UiLibraryModule, WorkflowDashboardNavigationComponent],
    templateUrl: "./platform-access-admin.component.html",
    styleUrl: "./platform-access-admin.component.scss"
})
export class PlatformAccessAdminComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformAccessAdminService = inject(PlatformAccessAdminService);
  private readonly roles = inject(RoleService);
  private navigationLoadSubscription?: Subscription;
  private navigationLoadRequestId = 0;

  @ViewChild("navigationNodeForm")
  private navigationNodeForm?: EzFormComponent;

  @ViewChild("navigationRuleForm")
  private navigationRuleForm?: EzFormComponent;

  readonly canRead = this.roles.hasAnyPermissionSignal(PLATFORM_ACCESS_ADMIN_READ);
  readonly canAuthor = this.roles.hasAnyPermissionSignal(PLATFORM_ACCESS_ADMIN_AUTHOR);
  readonly canPublish = this.roles.hasAnyPermissionSignal(PLATFORM_ACCESS_ADMIN_PUBLISH);
  readonly canRollback = this.roles.hasAnyPermissionSignal(PLATFORM_ACCESS_ADMIN_ROLLBACK);

  readonly isNavigationLoading = signal(false);
  readonly isNavigationSaving = signal(false);
  readonly activeActionKey = signal<string | null>(null);
  readonly catalogReloadTrigger = signal(0);
  readonly navigationParentNodes = signal<PlatformNavigationNodeDetail[]>([]);
  readonly selectedNavigationNode = signal<PlatformNavigationNodeDetail | null>(null);
  readonly isCreatingNavigationNode = signal(false);
  readonly navigationRuleDrafts = signal<PlatformNavigationRuleDraft[]>([]);
  readonly isRuleEditorVisible = signal(false);
  readonly editingRuleIndex = signal<number | null>(null);
  readonly successMessage = signal<string | null>(null);
  readonly errorState = signal<PlatformAccessAdminDisplayError | null>(null);

  readonly catalogColumnDefs: ColDef[] = this.buildCatalogColumnDefs();
  readonly catalogGridConfig: EzGridDsConfig = this.buildCatalogGridConfig();
  readonly catalogPanelHeaderConfig: PanelHeaderConfig = this.buildCatalogPanelHeaderConfig();
  readonly pagePanelSubtitle = "";
  readonly pagePanelHeaderConfig: PanelHeaderConfig = {
    additionalActions: [
      {
        name: "refreshCatalog",
        type: EzFormInputType.BUTTON,
        buttonText: "Refresh",
        disabled: this.isNavigationLoading,
      },
      {
        name: "beginCreateNavigationNode",
        type: EzFormInputType.BUTTON,
        buttonText: "New Node",
        disabled: () => !this.canAuthor() || this.isNavigationSaving(),
      },
    ],
    additionalActionsContainerClass: "gap-2 items-center compact",
  };

  readonly sortedNavigationRules = computed(() =>
    [...this.navigationRuleDrafts()].sort(compareRules),
  );

  readonly navigationEditBlockedReason = computed(() => {
    if (this.isCreatingNavigationNode()) {
      return null;
    }

    const node = this.selectedNavigationNode();
    if (!node) {
      return null;
    }

    switch (normalizeCode(node.statusCode)) {
      case "published":
        return "Published navigation nodes must be rolled back before they can be edited.";
      case "retired":
        return "Retired navigation nodes must be rolled back before they can be edited.";
      default:
        return null;
    }
  });

  readonly navigationRuleBlockedReason = computed(() => {
    if (this.isCreatingNavigationNode()) {
      return null;
    }

    const node = this.selectedNavigationNode();
    if (!node) {
      return "Select or create a navigation node before authoring visibility rules.";
    }

    return this.navigationEditBlockedReason();
  });

  readonly navigationNodeFormActionButtons = computed<ButtonConfig[]>(() => [
    {
      id: "saveNavigationNode",
      buttonText: this.isNavigationSaving()
        ? (this.isCreatingNavigationNode() ? "Creating..." : "Saving...")
        : (this.isCreatingNavigationNode() ? "Create Navigation Node" : "Save Navigation Node"),
      disabled: !this.canAuthor() || this.isNavigationSaving() || !!this.navigationEditBlockedReason(),
    },
    {
      id: "resetNavigationNodeForm",
      buttonText: "Reset",
      severity: "secondary",
      disabled: this.isNavigationSaving(),
    },
    {
      id: "publishSelectedNavigationNode",
      buttonText: this.isActionBusy("node:publish") ? "Publishing..." : "Publish",
      severity: "secondary",
      disabled: !this.canPublish() || !this.canPublishSelectedNavigationNode() || this.isNavigationSaving() || this.isActionBusy("node:publish") || !this.selectedNodeCode,
    },
    {
      id: "rollbackSelectedNavigationNode",
      buttonText: this.isActionBusy("node:rollback") ? "Rolling Back..." : "Rollback",
      severity: "secondary",
      disabled: !this.canRollback() || !this.canRollbackSelectedNavigationNode() || this.isNavigationSaving() || this.isActionBusy("node:rollback") || !this.selectedNodeCode,
    },
    {
      id: "retireSelectedNavigationNode",
      buttonText: this.isActionBusy("node:retire") ? "Retiring..." : "Retire",
      severity: "danger",
      disabled: !this.canRollback() || !this.canRetireSelectedNavigationNode() || this.isNavigationSaving() || this.isActionBusy("node:retire") || !this.selectedNodeCode,
    },
  ]);

  // The action bar is split into two logical clusters so the row of five buttons reads clearly:
  // the edit actions (Save / Reset) sit on the left, the lifecycle transitions (Publish / Rollback /
  // Retire) are pushed to the right. Both share onNavigationNodeFormActionButtonClick via button id.
  readonly navigationNodeEditButtons = computed<ButtonConfig[]>(() =>
    this.navigationNodeFormActionButtons().filter(
      (button) => button.id === "saveNavigationNode" || button.id === "resetNavigationNodeForm",
    ),
  );

  readonly navigationNodeLifecycleButtons = computed<ButtonConfig[]>(() =>
    this.navigationNodeFormActionButtons().filter(
      (button) =>
        button.id === "publishSelectedNavigationNode" ||
        button.id === "rollbackSelectedNavigationNode" ||
        button.id === "retireSelectedNavigationNode",
    ),
  );

  readonly isNavigationDetailEmpty = computed(
    () => !this.isCreatingNavigationNode() && !this.selectedNavigationNode(),
  );

  readonly navigationRuleFormActionButtons = computed<ButtonConfig[]>(() => [
    {
      id: "saveRule",
      buttonText: "Stage Rule",
      disabled: !this.canAuthor() || this.isNavigationSaving() || !!this.navigationRuleBlockedReason(),
    },
    {
      id: "cancelRuleEdit",
      buttonText: "Cancel",
      severity: "secondary",
      disabled: this.isNavigationSaving(),
    },
  ]);

  navigationNodeFormConfig: InputConfig[] = this.buildNavigationNodeFormConfig(true, false);
  navigationRuleFormConfig: InputConfig[] = this.buildNavigationRuleFormConfig();

  navigationNodeFormValues: PlatformNavigationNodeFormValues = this.createEmptyNavigationNodeFormValues();
  navigationRuleFormValues: PlatformNavigationRuleFormValues = this.createEmptyNavigationRuleFormValues();

  constructor() {
    this.loadNavigationParentNodes();
  }

  get selectedNodeCode(): string | null {
    return this.selectedNavigationNode()?.nodeCode ?? null;
  }

  get selectedNavigationStatusLabel(): string {
    return this.toStatusLabel(this.selectedNavigationNode()?.statusCode);
  }

  onPagePanelHeaderButtonClick(event: { name?: string }): void {
    switch (event.name) {
      case "refreshCatalog":
        this.refreshCatalog();
        break;
      case "beginCreateNavigationNode":
        this.beginCreateNavigationNode();
        break;
      default:
        break;
    }
  }

  onNavigationNodeFormActionButtonClick(button: ButtonConfig): void {
    switch (button.id) {
      case "saveNavigationNode":
        this.saveNavigationNode();
        break;
      case "resetNavigationNodeForm":
        this.resetNavigationNodeForm();
        break;
      case "publishSelectedNavigationNode":
        this.publishSelectedNavigationNode();
        break;
      case "retireSelectedNavigationNode":
        this.retireSelectedNavigationNode();
        break;
      case "rollbackSelectedNavigationNode":
        this.rollbackSelectedNavigationNode();
        break;
      default:
        break;
    }
  }

  onNavigationRuleFormActionButtonClick(button: ButtonConfig): void {
    switch (button.id) {
      case "saveRule":
        this.saveRule();
        break;
      case "cancelRuleEdit":
        this.cancelRuleEdit();
        break;
      default:
        break;
    }
  }

  refreshCatalog(): void {
    this.catalogReloadTrigger.update((current) => current + 1);
    this.loadNavigationParentNodes();
  }

  beginCreateNavigationNode(clearMessages = true): void {
    if (clearMessages) {
      this.clearMessages();
    }

    this.cancelNavigationDetailLoad();
    this.isCreatingNavigationNode.set(true);
    this.selectedNavigationNode.set(null);
    this.navigationRuleDrafts.set([]);
    this.navigationNodeFormValues = this.createEmptyNavigationNodeFormValues();
    this.navigationNodeFormConfig = this.buildNavigationNodeFormConfig(true, false, null);
    this.navigationNodeForm?.clearErrors();
    this.cancelRuleEdit(false);
  }

  selectNavigationNode(nodeCode: string, clearMessages = true): void {
    if (clearMessages) {
      this.clearMessages();
    }

    this.isCreatingNavigationNode.set(false);
    this.requestNavigationNode(nodeCode, { showLoading: true });
  }

  saveNavigationNode(): void {
    if (!this.canAuthor()) {
      this.errorState.set({
        title: "Access denied",
        messages: ["You do not have permission to author navigation metadata."],
        issueCodes: [],
      });
      return;
    }

    if (this.navigationEditBlockedReason()) {
      this.errorState.set({
        title: "Navigation node is read-only",
        messages: [this.navigationEditBlockedReason()!],
        issueCodes: [],
      });
      return;
    }

    this.clearMessages();
    if (!this.ensureFormIsValid(this.navigationNodeForm, "Resolve the navigation node form errors before saving.")) {
      return;
    }

    const isCreateMode = this.isCreatingNavigationNode();
    const request = this.buildNavigationNodeRequest(this.navigationNodeFormValues, isCreateMode);
    if (!request) {
      return;
    }

    this.isNavigationSaving.set(true);
    const request$ = isCreateMode
      ? this.platformAccessAdminService.createNavigationNode(request as PlatformNavigationNodeCreateRequest)
      : this.platformAccessAdminService.updateNavigationNode(this.selectedNodeCode!, request as PlatformNavigationNodeUpdateRequest);

    request$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (node) => {
          this.applyNavigationNode(node);
          this.isNavigationSaving.set(false);
          this.successMessage.set(
            isCreateMode
              ? "Navigation node created successfully."
              : "Navigation node updated successfully.",
          );
          this.refreshCatalog();
        },
        error: (error) => {
          this.isNavigationSaving.set(false);
          this.errorState.set(mapPlatformAccessAdminError(error));
        },
      });
  }

  publishSelectedNavigationNode(): void {
    if (!this.selectedNodeCode) {
      return;
    }

    this.triggerNavigationLifecycleAction(
      "node:publish",
      "Navigation node published successfully.",
      () => this.platformAccessAdminService.publishNavigationNode(this.selectedNodeCode!),
    );
  }

  retireSelectedNavigationNode(): void {
    if (!this.selectedNodeCode) {
      return;
    }

    this.triggerNavigationLifecycleAction(
      "node:retire",
      "Navigation node retired successfully.",
      () => this.platformAccessAdminService.retireNavigationNode(this.selectedNodeCode!),
    );
  }

  rollbackSelectedNavigationNode(): void {
    if (!this.selectedNodeCode) {
      return;
    }

    this.triggerNavigationLifecycleAction(
      "node:rollback",
      "Navigation node rolled back to draft successfully.",
      () => this.platformAccessAdminService.rollbackNavigationNode(this.selectedNodeCode!),
    );
  }

  startCreateRule(): void {
    if (this.navigationRuleBlockedReason()) {
      this.errorState.set({
        title: "Navigation rules are read-only",
        messages: [this.navigationRuleBlockedReason()!],
        issueCodes: [],
      });
      return;
    }

    this.isRuleEditorVisible.set(true);
    this.editingRuleIndex.set(null);
    this.navigationRuleFormValues = this.createEmptyNavigationRuleFormValues();
    this.navigationRuleForm?.clearErrors();
  }

  editRule(index: number): void {
    const rule = this.sortedNavigationRules()[index];
    if (!rule) {
      return;
    }

    this.isRuleEditorVisible.set(true);
    this.editingRuleIndex.set(index);
    this.navigationRuleFormValues = this.mapNavigationRuleToFormValues(rule);
    this.navigationRuleForm?.clearErrors();
  }

  saveRule(): void {
    if (this.navigationRuleBlockedReason()) {
      this.errorState.set({
        title: "Navigation rules are read-only",
        messages: [this.navigationRuleBlockedReason()!],
        issueCodes: [],
      });
      return;
    }

    if (!this.ensureFormIsValid(this.navigationRuleForm, "Resolve the visibility-rule form errors before saving.")) {
      return;
    }

    const nextRule = this.buildNavigationRuleDraft(this.navigationRuleFormValues);
    if (!nextRule) {
      return;
    }

    const rules = [...this.navigationRuleDrafts()];
    const editingIndex = this.editingRuleIndex();
    if (editingIndex === null) {
      rules.push(nextRule);
    } else {
      rules.splice(editingIndex, 1, {
        ...nextRule,
        id: rules[editingIndex]?.id,
      });
    }

    rules.sort(compareRules);
    this.navigationRuleDrafts.set(rules);
    this.cancelRuleEdit(false);
    this.successMessage.set("Navigation rule staged locally. Save the navigation node to persist the change.");
  }

  removeRule(index: number): void {
    if (this.navigationRuleBlockedReason()) {
      this.errorState.set({
        title: "Navigation rules are read-only",
        messages: [this.navigationRuleBlockedReason()!],
        issueCodes: [],
      });
      return;
    }

    const rules = [...this.navigationRuleDrafts()];
    rules.splice(index, 1);
    this.navigationRuleDrafts.set(rules);
    if (this.editingRuleIndex() === index) {
      this.cancelRuleEdit(false);
    }
  }

  resetNavigationNodeForm(): void {
    if (this.isCreatingNavigationNode()) {
      this.beginCreateNavigationNode(false);
      return;
    }

    const node = this.selectedNavigationNode();
    if (!node) {
      return;
    }

    this.navigationNodeFormValues = this.mapNavigationNodeToFormValues(node);
    this.navigationRuleDrafts.set([...node.visibilityRules].sort(compareRules));
    this.navigationNodeForm?.clearErrors();
    this.cancelRuleEdit(false);
  }

  cancelRuleEdit(clearMessages = true): void {
    if (clearMessages) {
      this.clearMessages();
    }

    this.isRuleEditorVisible.set(false);
    this.editingRuleIndex.set(null);
    this.navigationRuleFormValues = this.createEmptyNavigationRuleFormValues();
    this.navigationRuleForm?.clearErrors();
  }

  isActionBusy(actionKey: string): boolean {
    return this.activeActionKey() === actionKey;
  }

  canPublishSelectedNavigationNode(): boolean {
    return normalizeCode(this.selectedNavigationNode()?.statusCode) === "draft";
  }

  canRetireSelectedNavigationNode(): boolean {
    return normalizeCode(this.selectedNavigationNode()?.statusCode) === "published";
  }

  canRollbackSelectedNavigationNode(): boolean {
    const normalizedStatus = normalizeCode(this.selectedNavigationNode()?.statusCode);
    return normalizedStatus === "published" || normalizedStatus === "retired";
  }

  toStatusLabel(statusCode: string | null | undefined): string {
    const normalized = normalizeCode(statusCode);
    if (normalized.length === 0) {
      return "Unknown";
    }

    return normalized
      .split("_")
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(" ");
  }

  formatLifecycleWindow(effectiveStartUtc: string, effectiveEndUtc: string | null | undefined): string {
    const startLabel = formatDateTimeLabel(effectiveStartUtc);
    const endLabel = effectiveEndUtc ? formatDateTimeLabel(effectiveEndUtc) : "Open-ended";
    return `${startLabel} -> ${endLabel}`;
  }

  formatRuleTarget(rule: PlatformNavigationRuleDraft): string {
    if (normalizeCode(rule.ruleTypeCode) === "permission") {
      return normalizeOptionalText(rule.permissionCode) || "Permission code required";
    }

    if (normalizeCode(rule.ruleTypeCode) === "feature_flag") {
      return normalizeOptionalText(rule.featureFlagCode) || "Feature flag code required";
    }

    return "Unknown target";
  }

  private triggerNavigationLifecycleAction(
    actionKey: string,
    successMessage: string,
    action: () => ReturnType<PlatformAccessAdminService["publishNavigationNode"]>,
  ): void {
    if (!this.selectedNodeCode) {
      return;
    }

    this.clearMessages();
    this.isNavigationSaving.set(true);
    this.activeActionKey.set(actionKey);

    action()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (node) => {
          this.activeActionKey.set(null);
          this.isNavigationSaving.set(false);
          this.applyNavigationNode(node);
          this.successMessage.set(successMessage);
          this.refreshCatalog();
        },
        error: (error) => {
          this.activeActionKey.set(null);
          this.isNavigationSaving.set(false);
          this.errorState.set(mapPlatformAccessAdminError(error));
        },
      });
  }

  private applyNavigationNode(node: PlatformNavigationNodeDetail): void {
    this.selectedNavigationNode.set(node);
    this.isCreatingNavigationNode.set(false);
    this.navigationNodeFormValues = this.mapNavigationNodeToFormValues(node);
    this.navigationNodeFormConfig = this.buildNavigationNodeFormConfig(false, this.navigationEditBlockedReason() !== null, node.nodeCode);
    this.navigationNodeForm?.clearErrors();
    this.navigationRuleDrafts.set([...node.visibilityRules].sort(compareRules));
    this.cancelRuleEdit(false);
  }

  private requestNavigationNode(nodeCode: string, options: { showLoading: boolean }): void {
    this.navigationLoadSubscription?.unsubscribe();
    const requestId = ++this.navigationLoadRequestId;
    this.isNavigationLoading.set(options.showLoading);

    this.navigationLoadSubscription = this.platformAccessAdminService
      .getNavigationNode(nodeCode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (node) => {
          if (requestId !== this.navigationLoadRequestId) {
            return;
          }

          this.applyNavigationNode(node);
          this.isNavigationLoading.set(false);
        },
        error: (error) => {
          if (requestId !== this.navigationLoadRequestId) {
            return;
          }

          this.isNavigationLoading.set(false);
          this.errorState.set(mapPlatformAccessAdminError(error));
        },
      });
  }

  private cancelNavigationDetailLoad(): void {
    this.navigationLoadSubscription?.unsubscribe();
    this.navigationLoadSubscription = undefined;
    this.navigationLoadRequestId++;
    this.isNavigationLoading.set(false);
  }

  private ensureFormIsValid(form: EzFormComponent | undefined, fallbackMessage: string): boolean {
    const formGroup = form?.theFormGroup;
    if (!formGroup || formGroup.valid) {
      return true;
    }

    formGroup.markAllAsTouched();
    const controlErrors = form?.controlErrors?.map((entry) => entry.message) ?? [];
    this.errorState.set({
      title: "Validation failed",
      messages: controlErrors.length > 0 ? [...new Set(controlErrors)] : [fallbackMessage],
      issueCodes: [],
    });

    return false;
  }

  private loadNavigationParentNodes(): void {
    this.platformAccessAdminService
      .listNavigationNodes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (nodes) => {
          this.navigationParentNodes.set([...nodes].sort(compareNavigationParentOptions));
          this.refreshNavigationNodeFormConfig();
        },
        error: (error) => {
          this.navigationParentNodes.set([]);
          this.errorState.set(mapPlatformAccessAdminError(error));
          this.refreshNavigationNodeFormConfig();
        },
      });
  }

  private refreshNavigationNodeFormConfig(): void {
    this.navigationNodeFormConfig = this.buildNavigationNodeFormConfig(
      this.isCreatingNavigationNode(),
      this.navigationEditBlockedReason() !== null,
      this.selectedNodeCode,
    );
  }

  private buildNavigationNodeFormConfig(
    isCreateMode: boolean,
    readOnly: boolean,
    currentNodeCode?: string | null,
  ): InputConfig[] {
    // The 10 fields are grouped into four labeled sections. Each heading is an HTML-type "field"
    // (full width, its own row) rendered inline by ui-form via getResolvedHtml(control.label). HTML
    // fields get a harmless value-less control and are never read on save, so this keeps one form,
    // one validation path, and one formValues binding while giving the form real visual structure.
    return [
      this.buildFormSectionHeading("identity", "Identity", 1),
      {
        name: "nodeCode",
        type: EzFormInputType.TEXT,
        label: "Node Code",
        row: 2,
        width: "1x",
        readOnly: !isCreateMode,
        validation: isCreateMode ? [Validators.required] : [],
        validationMessages: {
          required: "Node code is required.",
        },
      },
      {
        name: "displayName",
        type: EzFormInputType.TEXT,
        label: "Display Name",
        row: 2,
        width: "1x",
        readOnly,
        validation: [Validators.required],
        validationMessages: {
          required: "Display name is required.",
        },
      },
      this.buildFormSectionHeading("placement", "Placement &amp; Ordering", 3),
      {
        name: "parentNodeCode",
        type: EzFormInputType.SELECT,
        label: "Parent Node Code",
        row: 4,
        width: "1x",
        readOnly,
        options: this.buildParentNodeOptions(currentNodeCode),
      },
      {
        name: "nodeTypeCode",
        type: EzFormInputType.SELECT,
        label: "Node Type",
        row: 4,
        width: "1x",
        readOnly,
        options: NAVIGATION_NODE_TYPE_OPTIONS,
        validation: [Validators.required],
        validationMessages: {
          required: "Node type is required.",
        },
      },
      {
        name: "groupCode",
        type: EzFormInputType.TEXT,
        label: "Group Code",
        row: 5,
        width: "1x",
        readOnly,
      },
      {
        name: "sortOrder",
        type: EzFormInputType.NUMBER,
        label: "Sort Order",
        row: 5,
        width: "1x",
        readOnly,
        validation: [Validators.required, Validators.min(0)],
        validationMessages: {
          required: "Sort order is required.",
          min: "Sort order must be zero or greater.",
        },
      },
      this.buildFormSectionHeading("routing", "Routing &amp; Presentation", 6),
      {
        name: "routePath",
        type: EzFormInputType.TEXT,
        label: "Route Path",
        row: 7,
        width: "1x",
        readOnly,
      },
      {
        name: "iconKey",
        type: EzFormInputType.TEXT,
        label: "Icon Key",
        row: 7,
        width: "1x",
        readOnly,
      },
      this.buildFormSectionHeading("lifecycle", "Lifecycle", 8),
      {
        name: "effectiveStartUtc",
        type: EzFormInputType.DATE_TIME,
        label: "Effective Start (Local Time)",
        row: 9,
        width: "1x",
        readOnly,
        validation: [Validators.required],
        validationMessages: {
          required: "Effective start is required.",
        },
        dateTimeConfig: {
          hourFormat: "24",
          showSeconds: false,
          showOnFocus: false,
          showClear: false,
        },
      },
      {
        name: "effectiveEndUtc",
        type: EzFormInputType.DATE_TIME,
        label: "Effective End (Local Time)",
        row: 9,
        width: "1x",
        readOnly,
        dateTimeConfig: {
          hourFormat: "24",
          showSeconds: false,
          showOnFocus: false,
          showClear: true,
        },
      },
    ];
  }

  // A full-width, non-input heading rendered inline inside the ui-form to delimit a field group.
  private buildFormSectionHeading(key: string, title: string, row: number): InputConfig {
    return {
      name: `_section_${key}`,
      type: EzFormInputType.HTML,
      label: `<span class="form-section-heading__label">${title}</span>`,
      customCssClasses: "form-section-heading",
      row,
      width: "fill",
    };
  }

  private buildNavigationRuleFormConfig(): InputConfig[] {
    return [
      {
        name: "ruleTypeCode",
        type: EzFormInputType.SELECT,
        label: "Rule Type",
        row: 1,
        width: "1x",
        options: NAVIGATION_RULE_TYPE_OPTIONS,
        validation: [Validators.required],
        validationMessages: {
          required: "Rule type is required.",
        },
      },
      {
        name: "visibilityModeCode",
        type: EzFormInputType.SELECT,
        label: "Visibility Mode",
        row: 1,
        width: "1x",
        options: NAVIGATION_VISIBILITY_MODE_OPTIONS,
        validation: [Validators.required],
        validationMessages: {
          required: "Visibility mode is required.",
        },
      },
      {
        name: "permissionCode",
        type: EzFormInputType.TEXT,
        label: "Permission Code",
        row: 2,
        width: "1x",
      },
      {
        name: "featureFlagCode",
        type: EzFormInputType.TEXT,
        label: "Feature Flag Code",
        row: 2,
        width: "1x",
      },
      {
        name: "sortOrder",
        type: EzFormInputType.NUMBER,
        label: "Rule Sort Order",
        row: 3,
        width: "1x",
        validation: [Validators.required, Validators.min(0)],
        validationMessages: {
          required: "Rule sort order is required.",
          min: "Rule sort order must be zero or greater.",
        },
      },
      {
        name: "effectiveStartUtc",
        type: EzFormInputType.DATE_TIME,
        label: "Effective Start (Local Time)",
        row: 4,
        width: "1x",
        validation: [Validators.required],
        validationMessages: {
          required: "Rule effective start is required.",
        },
        dateTimeConfig: {
          hourFormat: "24",
          showSeconds: false,
          showOnFocus: false,
          showClear: false,
        },
      },
      {
        name: "effectiveEndUtc",
        type: EzFormInputType.DATE_TIME,
        label: "Effective End (Local Time)",
        row: 4,
        width: "1x",
        dateTimeConfig: {
          hourFormat: "24",
          showSeconds: false,
          showOnFocus: false,
          showClear: true,
        },
      },
    ];
  }

  private buildParentNodeOptions(currentNodeCode?: string | null): InputSelectOption[] {
    const normalizedCurrentNodeCode = normalizeCode(currentNodeCode);
    const parentOptions = this.navigationParentNodes()
      .filter((node) => normalizeCode(node.nodeCode) !== normalizedCurrentNodeCode)
      .map((node) => ({
        value: node.nodeCode,
        viewValue: node.parentNodeCode
          ? `${node.displayName} (${node.nodeCode})`
          : `${node.displayName} (${node.nodeCode}, root)`,
      }));

    return [
      { value: "", viewValue: "No parent" },
      ...parentOptions,
    ];
  }

  private createEmptyNavigationNodeFormValues(): PlatformNavigationNodeFormValues {
    return {
      nodeCode: "",
      parentNodeCode: "",
      nodeTypeCode: "route",
      displayName: "",
      routePath: "",
      iconKey: "",
      groupCode: "",
      sortOrder: 0,
      effectiveStartUtc: new Date(),
      effectiveEndUtc: null,
    };
  }

  private createEmptyNavigationRuleFormValues(): PlatformNavigationRuleFormValues {
    return {
      ruleTypeCode: "permission",
      permissionCode: "",
      featureFlagCode: "",
      visibilityModeCode: "required",
      sortOrder: 0,
      effectiveStartUtc: new Date(),
      effectiveEndUtc: null,
    };
  }

  private buildNavigationNodeRequest(
    values: PlatformNavigationNodeFormValues,
    isCreateMode: boolean,
  ): PlatformNavigationNodeCreateRequest | PlatformNavigationNodeUpdateRequest | null {
    const effectiveStartUtc = this.toIsoDateTime(values.effectiveStartUtc, "Navigation node effective start");
    if (!effectiveStartUtc) {
      return null;
    }

    const sortOrder = parseNonNegativeInteger(values.sortOrder);
    if (sortOrder === null) {
      this.errorState.set({
        title: "Validation failed",
        messages: ["Navigation sort order must be zero or greater."],
        issueCodes: [],
      });
      return null;
    }

    const baseRequest = {
      parentNodeCode: normalizeOptionalText(values.parentNodeCode),
      nodeTypeCode: values.nodeTypeCode.trim(),
      displayName: values.displayName.trim(),
      routePath: normalizeOptionalText(values.routePath),
      iconKey: normalizeOptionalText(values.iconKey),
      groupCode: normalizeOptionalText(values.groupCode),
      sortOrder,
      effectiveStartUtc,
      effectiveEndUtc: this.toIsoDateTime(values.effectiveEndUtc, "Navigation node effective end", true),
      visibilityRules: this.navigationRuleDrafts().map((rule) => this.mapRuleDraftToRequest(rule)),
    };

    if (!isCreateMode) {
      return baseRequest;
    }

    return {
      ...baseRequest,
      nodeCode: values.nodeCode.trim(),
    };
  }

  private buildNavigationRuleDraft(values: PlatformNavigationRuleFormValues): PlatformNavigationRuleDraft | null {
    const effectiveStartUtc = this.toIsoDateTime(values.effectiveStartUtc, "Visibility rule effective start");
    if (!effectiveStartUtc) {
      return null;
    }

    const sortOrder = parseNonNegativeInteger(values.sortOrder);
    if (sortOrder === null) {
      this.errorState.set({
        title: "Validation failed",
        messages: ["Visibility rule sort order must be zero or greater."],
        issueCodes: [],
      });
      return null;
    }

    return {
      ruleTypeCode: values.ruleTypeCode.trim(),
      permissionCode: normalizeOptionalText(values.permissionCode),
      featureFlagCode: normalizeOptionalText(values.featureFlagCode),
      visibilityModeCode: values.visibilityModeCode.trim(),
      sortOrder,
      effectiveStartUtc,
      effectiveEndUtc: this.toIsoDateTime(values.effectiveEndUtc, "Visibility rule effective end", true),
    };
  }

  private mapNavigationNodeToFormValues(node: PlatformNavigationNodeDetail): PlatformNavigationNodeFormValues {
    return {
      nodeCode: node.nodeCode,
      parentNodeCode: node.parentNodeCode ?? "",
      nodeTypeCode: node.nodeTypeCode,
      displayName: node.displayName,
      routePath: node.routePath ?? "",
      iconKey: node.iconKey ?? "",
      groupCode: node.groupCode ?? "",
      sortOrder: node.sortOrder,
      effectiveStartUtc: new Date(node.effectiveStartUtc),
      effectiveEndUtc: node.effectiveEndUtc ? new Date(node.effectiveEndUtc) : null,
    };
  }

  private mapNavigationRuleToFormValues(rule: PlatformNavigationRuleDraft): PlatformNavigationRuleFormValues {
    return {
      ruleTypeCode: rule.ruleTypeCode ?? "",
      permissionCode: rule.permissionCode ?? "",
      featureFlagCode: rule.featureFlagCode ?? "",
      visibilityModeCode: rule.visibilityModeCode ?? "",
      sortOrder: rule.sortOrder,
      effectiveStartUtc: new Date(rule.effectiveStartUtc),
      effectiveEndUtc: rule.effectiveEndUtc ? new Date(rule.effectiveEndUtc) : null,
    };
  }

  private mapRuleDraftToRequest(rule: PlatformNavigationRuleDraft): PlatformNavigationRuleRequest {
    return {
      ruleTypeCode: rule.ruleTypeCode,
      permissionCode: normalizeOptionalText(rule.permissionCode),
      featureFlagCode: normalizeOptionalText(rule.featureFlagCode),
      visibilityModeCode: rule.visibilityModeCode,
      sortOrder: rule.sortOrder,
      effectiveStartUtc: rule.effectiveStartUtc,
      effectiveEndUtc: normalizeOptionalText(rule.effectiveEndUtc),
    };
  }

  private toIsoDateTime(value: string | Date | null, label: string, allowEmpty = false): string | null {
    if (!value) {
      if (allowEmpty) {
        return null;
      }

      this.errorState.set({
        title: "Validation failed",
        messages: [`${label} is required.`],
        issueCodes: [],
      });
      return null;
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      this.errorState.set({
        title: "Validation failed",
        messages: [`${label} is not a valid date or time.`],
        issueCodes: [],
      });
      return null;
    }

    return date.toISOString();
  }

  private clearMessages(): void {
    this.successMessage.set(null);
    this.errorState.set(null);
  }

  private buildCatalogColumnDefs(): ColDef[] {
    return [
      { field: "displayName", headerName: "Name", flex: 1, minWidth: 140 },
      { field: "nodeCode", headerName: "Node Code", flex: 1, minWidth: 130 },
      { field: "statusCode", headerName: "Status", width: 110, valueFormatter: ({ value }) => this.toStatusLabel(value as string) },
      { field: "nodeTypeCode", headerName: "Type", width: 95 },
      {
        field: "sortOrder",
        headerName: "Sort",
        width: 80,
      },
      {
        headerName: "",
        width: 90,
        maxWidth: 90,
        sortable: false,
        cellRenderer: ButtonCellRenderer,
        cellRendererParams: {
          label: "Select",
          onClick: (params: { rowData: { nodeCode: string } }) => this.selectNavigationNode(params.rowData.nodeCode),
        },
      },
    ];
  }

  private buildCatalogGridConfig(): EzGridDsConfig {
    return {
      datasourceId: "platformNavigationNodes",
      bindings: [
        {
          bindingType: "READ",
          endpoint: this.platformAccessAdminService.getNavigationODataEndpoint(),
          protocol: "GET_ODATA",
        },
      ],
      colDefs: this.catalogColumnDefs,
    };
  }

  private buildCatalogPanelHeaderConfig(): PanelHeaderConfig {
    // The standard filter-control cluster (Clear Filters / Auto-apply / Manage Presets / preset
    // dropdown / Export to Excel) and the Display Name / Node Code filter inputs were removed at the
    // user's request — they were cramped by the EZ panel-header layout. The catalog grid is small
    // and sortable, so it stands on its own with just the title.
    return {
      panelHeaderTitle: "Navigation Catalog",
      titleClickToReload: true,
    };
  }
}

function compareRules(left: PlatformNavigationRuleDraft, right: PlatformNavigationRuleDraft): number {
  if (left.sortOrder !== right.sortOrder) {
    return left.sortOrder - right.sortOrder;
  }

  return normalizeCode(left.ruleTypeCode).localeCompare(normalizeCode(right.ruleTypeCode));
}

function compareNavigationParentOptions(left: PlatformNavigationNodeDetail, right: PlatformNavigationNodeDetail): number {
  if ((left.parentNodeCode ?? "") !== (right.parentNodeCode ?? "")) {
    return (left.parentNodeCode ?? "").localeCompare(right.parentNodeCode ?? "");
  }

  if (left.sortOrder !== right.sortOrder) {
    return left.sortOrder - right.sortOrder;
  }

  return left.displayName.localeCompare(right.displayName);
}

function normalizeCode(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

function normalizeOptionalText(value: string | null | undefined): string | null {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : null;
}

function parseNonNegativeInteger(value: number | string | null): number | null {
  if (value === null || value === "") {
    return null;
  }

  const parsed = typeof value === "number" ? value : Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
}

function formatDateTimeLabel(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
}
